import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'

interface Props {
  open: boolean
  onClose: () => void
}

const RECENT_KEY = 'aska-spotlight-recent'
const MAX_RECENT = 5

const QUICK_PROMPTS = [
  'Round 1-2ct under $5k',
  'Oval D color VS+',
  'Biggest stone in stock',
  'Heart shape in stock',
]

/**
 * ⌘K Spotlight modal — quick chat entry from anywhere on the site.
 * Type a query, hit Enter, lands on /chat?q=… with the message
 * pre-loaded. Recent queries cached in localStorage so frequent
 * searches surface as one-click chips.
 *
 * Mounted globally in App.tsx; opens on ⌘K / Ctrl+K, closes on Esc
 * or click outside.
 */
export function Spotlight({ open, onClose }: Props) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [recent, setRecent] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setQuery('')
    try {
      const raw = localStorage.getItem(RECENT_KEY)
      setRecent(raw ? (JSON.parse(raw) as string[]).slice(0, MAX_RECENT) : [])
    } catch { setRecent([]) }
    // Focus the input on the next frame so the animation can play.
    requestAnimationFrame(() => inputRef.current?.focus())
  }, [open])

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  function go(q: string) {
    const trimmed = q.trim()
    if (!trimmed) return
    try {
      const next = [trimmed, ...recent.filter((r) => r !== trimmed)].slice(0, MAX_RECENT)
      localStorage.setItem(RECENT_KEY, JSON.stringify(next))
    } catch { /* ignore quota */ }
    onClose()
    navigate(`/chat?q=${encodeURIComponent(trimmed)}`)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      go(query)
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 backdrop-blur-sm pt-[10vh] px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={onClose}
          data-testid="spotlight"
        >
          <motion.div
            initial={{ scale: 0.95, y: -8 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: -8 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="w-full max-w-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Spotlight — quick ask"
          >
            <div className="rounded-2xl bg-paper shadow-soft border border-border overflow-hidden">
              <div className="flex items-center gap-3 border-b border-border px-4 py-3">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="text-soft">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.3-4.3" />
                </svg>
                <input
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Ask DiaEx AI — describe your dream stone…"
                  className="flex-1 bg-transparent text-[15px] text-ink placeholder:text-soft focus:outline-none"
                  data-testid="spotlight-input"
                />
                <kbd className="hidden sm:inline-flex h-6 items-center rounded-md border border-border bg-page px-1.5 text-[10px] font-semibold text-soft">
                  Esc
                </kbd>
              </div>

              <div className="max-h-[50vh] overflow-y-auto p-2">
                {query.trim() ? (
                  <button
                    type="button"
                    onClick={() => go(query)}
                    className="flex items-center justify-between w-full rounded-lg px-3 py-2.5 text-left hover:bg-page transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" className="text-primary">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <polyline points="5 12 12 5 19 12" />
                      </svg>
                      <span className="text-sm text-ink">Ask “{query.trim()}”</span>
                    </span>
                    <kbd className="inline-flex h-6 items-center rounded-md border border-border bg-page px-1.5 text-[10px] font-semibold text-soft">↵</kbd>
                  </button>
                ) : (
                  <>
                    {recent.length > 0 && (
                      <Section title="Recent">
                        {recent.map((r) => (
                          <Row key={r} label={r} onClick={() => go(r)} icon="clock" />
                        ))}
                      </Section>
                    )}
                    <Section title="Try">
                      {QUICK_PROMPTS.map((q) => (
                        <Row key={q} label={q} onClick={() => go(q)} icon="sparkle" />
                      ))}
                    </Section>
                  </>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-border px-4 py-2 bg-page text-[10px] uppercase tracking-[0.14em] text-soft font-semibold">
                <span>DiaEx Spotlight</span>
                <span className="flex items-center gap-1">
                  <kbd className="rounded bg-paper border border-border px-1 text-[9px]">⌘</kbd>
                  <kbd className="rounded bg-paper border border-border px-1 text-[9px]">K</kbd>
                  <span>to open anywhere</span>
                </span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="px-2 py-2">
      <p className="text-[10px] uppercase tracking-[0.16em] text-soft font-semibold px-2 mb-1">{title}</p>
      {children}
    </div>
  )
}

function Row({ label, onClick, icon }: { label: string; onClick: () => void; icon: 'clock' | 'sparkle' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-3 w-full rounded-lg px-3 py-2 text-left hover:bg-paper transition-colors"
    >
      {icon === 'clock' ? (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" className="text-soft">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
          <path d="M12 3l1.9 5.8L20 10l-5 4.4L16.4 21 12 17.7 7.6 21 9 14.4 4 10l6.1-1.2L12 3z" />
        </svg>
      )}
      <span className="text-sm text-ink">{label}</span>
    </button>
  )
}
